/**
 * Raketech Per-Account Page
 * Flat table of all accounts (not grouped) with sorting and filtering.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Scale,
  Download,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Filter,
  Search,
  Database,
  FileText,
  RefreshCw,
  Link2,
  Unlink,
  ArrowUpRight,
  ArrowDownRight,
  ArrowUp,
  ArrowDown,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ComparisonIssue {
  type: string;
  severity: string;
  accountId: string;
  username: string;
  brand: string;
  partner: string;
  offer: string;
  geo: string;
  currency: string;
  details: string;
  partnerGeoKey: string;

  invoiceFTDs?: number;
  invoiceRevenue?: number;
  reconciliationFTDs?: number;
  reconciliationEarningsOriginal?: number;
  reconciliationEarnings?: number;
  reconciliationRows?: number;
  partnerFTDs?: number;
  partnerCommissionOriginal?: number;
  partnerCommission?: number;
  partnerCommissionOurShare?: number;
  partnerSource?: string;

  adwstatsCommission?: number;
  adwstatsCommissionOriginal?: number;
  adwstatsCurrency?: string;
  adwstatsAccountId?: string;
  adwstatsAccountName?: string;
  adwstatsFTDs?: number;
  adwstatsMatchMethod?: string;
  adwstatsDataSynced?: boolean;

  manualCheckAmount?: number;
  manualCheckComment?: string;
  manualCheckSource?: string;

  ftdDifference?: number;
  revenueDifference?: number;
  percentageDifference?: number;
  exchangeRate?: number;

  missingInInvoice?: boolean;
  missingInReconciliation?: boolean;
  missingInPartner?: boolean;

  invoiceBrandKey?: string;
}

interface CompareData {
  period: string;
  exchangeRates: Record<string, number>;
  summary: {
    totalAccounts: number;
    matched: number;
    critical: number;
    underpaid: number;
    overpaid: number;
    missingInInvoice: number;
    missingInRecon: number;
    missingInPartner: number;
    noAccountsTracked: number;
  };
  totals: {
    invoiced: number;
    actualInvoiced: number;
    actualInvoiceCount: number;
    shouldBe: number;
    reconEarnings: number;
    partnerOurShare: number;
    adwstatsSynced: number;
    manualCheck: number;
    positiveCorrections: number;
    negativeCorrections: number;
  };
  adwstatsMatching: {
    matched: number;
    unmatched: number;
    matchedNotSynced: number;
    unmatchedAccounts: { accountId: string; username: string; partner: string; geo: string }[];
    totalLocalAccounts: number;
  };
  issues: ComparisonIssue[];
  offerMatchLog?: { invoiceBrand: string; invoiceOffer: string; invoiceGeo: string; invoicePartner: string; invoiceRevenue: number; matchedAccountId: string; matchedUsername: string; matchedBrand: string; matchedPartner: string }[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getAuthToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) {
      const parsed = JSON.parse(auth);
      return parsed.state?.token || null;
    }
  } catch { /* ignore */ }
  return null;
};

const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);

type TabFilter = 'all' | 'critical' | 'underpaid' | 'overpaid' | 'missing_invoice' | 'missing_recon' | 'missing_partner' | 'matches';
type SortField = 'severity' | 'accountId' | 'username' | 'partner' | 'brand' | 'offer' | 'geo' | 'currency' | 'invoiceRevenue' | 'reconciliation' | 'synced' | 'manualCheck' | 'diff' | 'pct';
type SortDir = 'asc' | 'desc';

const SEVERITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const RaketechAccounts: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [compareData, setCompareData] = useState<CompareData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('severity');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Invoice brand mapping modal
  const [linkModalIssue, setLinkModalIssue] = useState<ComparisonIssue | null>(null);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkSaving, setLinkSaving] = useState(false);

  const fetchComparison = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { toast.error('Not authenticated'); return; }
    setIsLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/raketech/compare', { headers: { Authorization: `Bearer ${token}` } });
      const json = await resp.json() as any;
      if (!json.success) throw new Error(json.error || json.message || 'Comparison failed');
      if (json.data.empty) { setError(json.data.message); setCompareData(null); }
      else setCompareData(json.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchComparison(); }, [fetchComparison]);

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir(field === 'severity' ? 'asc' : 'desc'); }
  };

  const filteredIssues = useMemo(() => {
    if (!compareData) return [];
    let items = compareData.issues;

    switch (activeTab) {
      case 'critical': items = items.filter(i => i.severity === 'critical'); break;
      case 'underpaid': items = items.filter(i => i.type === 'underpayment'); break;
      case 'overpaid': items = items.filter(i => (i.revenueDifference || 0) > 1); break;
      case 'missing_invoice': items = items.filter(i => i.missingInInvoice && !i.missingInReconciliation); break;
      case 'missing_recon': items = items.filter(i => i.missingInReconciliation); break;
      case 'missing_partner': items = items.filter(i => i.missingInPartner && !i.missingInReconciliation); break;
      case 'matches': items = items.filter(i => i.type === 'match'); break;
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(i =>
        i.accountId?.toLowerCase().includes(q) ||
        i.username?.toLowerCase().includes(q) ||
        i.brand?.toLowerCase().includes(q) ||
        i.partner?.toLowerCase().includes(q) ||
        i.offer?.toLowerCase().includes(q) ||
        i.geo?.toLowerCase().includes(q) ||
        i.details?.toLowerCase().includes(q) ||
        i.adwstatsAccountName?.toLowerCase().includes(q)
      );
    }

    const getVal = (iss: ComparisonIssue): number | string => {
      switch (sortField) {
        case 'severity': return SEVERITY_ORDER[iss.severity] ?? 9;
        case 'accountId': return iss.accountId || '';
        case 'username': return (iss.username || '').toLowerCase();
        case 'partner': return (iss.partner || '').toLowerCase();
        case 'brand': return (iss.brand || '').toLowerCase();
        case 'offer': return (iss.offer || '').toLowerCase();
        case 'geo': return (iss.geo || '').toLowerCase();
        case 'currency': return (iss.currency || '').toLowerCase();
        case 'invoiceRevenue': return iss.invoiceRevenue || 0;
        case 'reconciliation': return iss.reconciliationEarnings || 0;
        case 'synced': return iss.adwstatsCommission || 0;
        case 'manualCheck': return iss.manualCheckAmount || 0;
        case 'diff': return iss.revenueDifference || 0;
        case 'pct': return iss.percentageDifference || 0;
        default: return 0;
      }
    };

    return [...items].sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [compareData, activeTab, searchTerm, sortField, sortDir]);

  // Recon accounts available as link targets (those with accountId and recon data)
  const reconAccounts = useMemo(() => {
    if (!compareData) return [];
    return compareData.issues
      .filter(i => i.accountId && i.reconciliationEarnings !== undefined)
      .map(i => ({ accountId: i.accountId, username: i.username, partner: i.partner, brand: i.brand, geo: i.geo }))
      .sort((a, b) => (a.partner || '').localeCompare(b.partner || '') || (a.username || '').localeCompare(b.username || ''));
  }, [compareData]);

  const filteredReconAccounts = useMemo(() => {
    if (!linkSearch) return reconAccounts;
    const q = linkSearch.toLowerCase();
    return reconAccounts.filter(a =>
      a.accountId?.toLowerCase().includes(q) ||
      a.username?.toLowerCase().includes(q) ||
      a.partner?.toLowerCase().includes(q) ||
      a.brand?.toLowerCase().includes(q) ||
      a.geo?.toLowerCase().includes(q)
    );
  }, [reconAccounts, linkSearch]);

  const handleLinkSave = useCallback(async (target: { accountId: string; username: string }) => {
    if (!linkModalIssue?.invoiceBrandKey) return;
    const token = getAuthToken();
    if (!token) { toast.error('Not authenticated'); return; }
    setLinkSaving(true);
    try {
      const resp = await fetch('/api/raketech/invoice-mapping', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoice_brand_key: linkModalIssue.invoiceBrandKey,
          recon_account_id: target.accountId,
          recon_username: target.username,
          brand_display: linkModalIssue.brand || '',
        }),
      });
      const json = await resp.json() as any;
      if (!json.success) throw new Error(json.error || 'Failed to save mapping');
      toast.success(`Linked "${linkModalIssue.brand}" to account ${target.accountId}`);
      setLinkModalIssue(null);
      setLinkSearch('');
      fetchComparison();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLinkSaving(false);
    }
  }, [linkModalIssue, fetchComparison]);

  const SortHeader: React.FC<{ field: SortField; label: string; align?: string }> = ({ field, label, align }) => (
    <th
      className={`px-3 py-2 text-zinc-500 font-medium cursor-pointer hover:text-zinc-300 select-none whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field && (
          sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        )}
      </span>
    </th>
  );

  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <Scale className="h-7 w-7 text-purple-400" />
              Per Account View
            </h1>
            <p className="text-zinc-400 mt-1 text-sm sm:text-base">
              Flat list of all accounts with sorting (Mar 2023 – Nov 2024)
            </p>
          </div>
          <button
            onClick={fetchComparison}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors disabled:opacity-50 shrink-0"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Loading */}
      {isLoading && !compareData && (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-zinc-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading comparison data...</span>
          </div>
        </div>
      )}

      {/* Error / empty */}
      {error && !isLoading && (
        <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">No Data Available</h2>
          <p className="text-sm text-zinc-400">{error}</p>
        </div>
      )}

      {/* Main content */}
      {compareData && (
        <div className="space-y-6 animate-fade-in">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {compareData.totals.actualInvoiced > 0 && (
              <SummaryCard label="Actual Invoiced" value={fmtCurrency(compareData.totals.actualInvoiced)} icon={<FileText className="h-4 w-4" />} color="white" />
            )}
            <SummaryCard label="Commission Sheets" value={fmtCurrency(compareData.totals.invoiced)} icon={<FileText className="h-4 w-4" />} color="zinc" />
            <SummaryCard label="Reconciliation" value={fmtCurrency(compareData.totals.shouldBe)} icon={<Scale className="h-4 w-4" />} color="purple" />
            <SummaryCard label="Recon Earnings" value={fmtCurrency(compareData.totals.reconEarnings)} icon={<ArrowUpRight className="h-4 w-4" />} color="amber" />
            <SummaryCard label="Partner 95%" value={fmtCurrency(compareData.totals.partnerOurShare)} icon={<ArrowDownRight className="h-4 w-4" />} color="blue" />
            <SummaryCard label="Our Synced (AC)" value={fmtCurrency(compareData.totals.adwstatsSynced)} icon={<Database className="h-4 w-4" />} color="emerald" />
            {compareData.totals.manualCheck > 0 && (
              <SummaryCard label="Manual Check" value={fmtCurrency(compareData.totals.manualCheck)} icon={<CheckCircle2 className="h-4 w-4" />} color="cyan" />
            )}
          </div>

          {/* Tabs + Search */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
              <Filter className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
              {([
                ['all', `All (${compareData.summary.totalAccounts})`],
                ['critical', `Critical (${compareData.summary.critical})`],
                ['underpaid', `Underpaid (${compareData.summary.underpaid})`],
                ['overpaid', `Overpaid (${compareData.summary.overpaid})`],
                ['missing_invoice', `No Invoice (${compareData.summary.missingInInvoice})`],
                ['missing_recon', `No Recon (${compareData.summary.missingInRecon})`],
                ['missing_partner', `No Partner (${compareData.summary.missingInPartner})`],
                ['matches', `Matches (${compareData.summary.matched})`],
              ] as [TabFilter, string][]).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`px-2.5 py-1 text-xs rounded-md whitespace-nowrap transition-colors ${
                    activeTab === key
                      ? 'bg-purple-500/10 text-purple-400 border border-purple-500/30'
                      : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex-1" />
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search accounts..."
                className="pl-8 pr-3 py-1.5 w-56 text-xs bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-purple-500/50"
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
            <div className="p-4 border-b border-[hsl(225,15%,18%)]">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Scale className="h-4 w-4 text-purple-400" />
                All Accounts ({filteredIssues.length})
              </h3>
            </div>

            {filteredIssues.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm">No results match your filters</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[hsl(225,15%,8%)] sticky top-0">
                    <tr>
                      <SortHeader field="severity" label="Severity" />
                      <SortHeader field="accountId" label="Account ID" />
                      <SortHeader field="username" label="Username" />
                      <SortHeader field="partner" label="Partner" />
                      <SortHeader field="brand" label="Brand" />
                      <SortHeader field="offer" label="Offer" />
                      <SortHeader field="geo" label="GEO" />
                      <SortHeader field="currency" label="Curr" />
                      <SortHeader field="invoiceRevenue" label="Invoiced (EUR)" align="right" />
                      <SortHeader field="reconciliation" label="Reconciliation" align="right" />
                      <SortHeader field="synced" label="Our Synced" align="right" />
                      <SortHeader field="manualCheck" label="Manual Check" align="right" />
                      <SortHeader field="diff" label="Diff" align="right" />
                      <SortHeader field="pct" label="%" align="right" />
                      <th className="text-left px-3 py-2 text-zinc-500 font-medium">Account</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                    {filteredIssues.map((iss, idx) => {
                      const diff = iss.revenueDifference || 0;
                      const diffColor = Math.abs(diff) < 1 ? 'text-zinc-600' : diff < 0 ? 'text-red-400' : 'text-emerald-400';

                      return (
                        <tr key={`${iss.accountId}-${idx}`} className="hover:bg-[hsl(225,15%,12%)]">
                          <td className="px-3 py-2"><SeverityBadge severity={iss.severity} /></td>
                          <td className="px-3 py-2 text-zinc-300 tabular-nums">{iss.accountId || '—'}</td>
                          <td className="px-3 py-2 text-white font-medium">{iss.username || '—'}</td>
                          <td className="px-3 py-2 text-zinc-400 max-w-[140px] truncate" title={iss.partner}>{iss.partner || '—'}</td>
                          <td className="px-3 py-2 text-zinc-400 max-w-[120px] truncate" title={iss.brand}>{iss.brand || '—'}</td>
                          <td className="px-3 py-2 text-zinc-500 max-w-[120px] truncate" title={(iss as any).offers ? (iss as any).offers.join(', ') : iss.offer}>{iss.offer || '—'}</td>
                          <td className="px-3 py-2 text-purple-400 font-medium">{iss.geo || '—'}</td>
                          <td className="px-3 py-2 text-zinc-500">{iss.currency || '—'}</td>
                          <td className="px-3 py-2 text-right text-zinc-300 tabular-nums">
                            {iss.invoiceRevenue !== undefined ? fmtCurrency(iss.invoiceRevenue) : '—'}
                          </td>
                          <td className="px-3 py-2 text-right text-amber-400 tabular-nums">
                            {iss.reconciliationEarnings !== undefined ? fmtCurrency(iss.reconciliationEarnings) : '—'}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {iss.adwstatsAccountId ? (
                              iss.adwstatsDataSynced === false ? (
                                <span className="text-amber-400/70 text-[10px]">not synced</span>
                              ) : (
                                <span className="text-emerald-400">
                                  {fmtCurrency(iss.adwstatsCommission || 0)}
                                  {iss.adwstatsCurrency && iss.adwstatsCurrency !== 'EUR' && (
                                    <span className="block text-[9px] text-emerald-400/50">{iss.adwstatsCurrency}</span>
                                  )}
                                </span>
                              )
                            ) : <span className="text-zinc-600">—</span>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {iss.manualCheckAmount ? (
                              <span className="text-cyan-400" title={iss.manualCheckComment || iss.manualCheckSource || ''}>
                                {fmtCurrency(iss.manualCheckAmount)}
                                {iss.manualCheckSource && (
                                  <span className="block text-[9px] text-cyan-400/60">{iss.manualCheckSource}</span>
                                )}
                              </span>
                            ) : <span className="text-zinc-600">—</span>}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums font-medium ${diffColor}`}>
                            {iss.revenueDifference !== undefined ? fmtCurrency(diff) : '—'}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums ${diffColor}`}>
                            {iss.percentageDifference !== undefined ? `${iss.percentageDifference.toFixed(1)}%` : '—'}
                          </td>
                          <td className="px-3 py-2">
                            {iss.adwstatsAccountName ? (
                              <span className="flex items-center gap-1">
                                <Link2 className={`h-3 w-3 shrink-0 ${iss.adwstatsDataSynced === false ? 'text-amber-500' : 'text-emerald-500'}`} />
                                <span className="text-zinc-300 truncate max-w-[120px]" title={iss.adwstatsAccountName}>
                                  {iss.adwstatsAccountName}
                                </span>
                                {iss.adwstatsDataSynced === false ? (
                                  <span className="text-[9px] text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1 rounded shrink-0">not synced</span>
                                ) : (
                                  <span className="text-[9px] text-zinc-600 shrink-0">({iss.adwstatsMatchMethod})</span>
                                )}
                              </span>
                            ) : iss.type === 'no_accounts_tracked' && iss.invoiceBrandKey ? (
                              <button
                                onClick={() => { setLinkModalIssue(iss); setLinkSearch(''); }}
                                className="flex items-center gap-1 text-purple-400 hover:text-purple-300 transition-colors text-[10px] border border-purple-500/30 bg-purple-500/10 px-1.5 py-0.5 rounded"
                              >
                                <Link2 className="h-3 w-3" />
                                Link
                              </button>
                            ) : iss.accountId ? (
                              <span className="text-zinc-600 flex items-center gap-1">
                                <Unlink className="h-3 w-3" />
                                <span className="text-[10px]">unmatched</span>
                              </span>
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Offer Match Log */}
          {compareData.offerMatchLog && compareData.offerMatchLog.length > 0 && (
            <details className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
              <summary className="p-4 cursor-pointer text-sm font-semibold text-white hover:bg-[hsl(225,15%,12%)]">
                Offer ID Auto-Matches ({compareData.offerMatchLog.length}) — click to verify
              </summary>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[hsl(225,15%,8%)]">
                    <tr>
                      <th className="px-3 py-2 text-left text-zinc-500 font-medium">Invoice Brand</th>
                      <th className="px-3 py-2 text-left text-zinc-500 font-medium">Offer ID</th>
                      <th className="px-3 py-2 text-left text-zinc-500 font-medium">Invoice Partner</th>
                      <th className="px-3 py-2 text-left text-zinc-500 font-medium">GEO</th>
                      <th className="px-3 py-2 text-right text-zinc-500 font-medium">Revenue</th>
                      <th className="px-3 py-2 text-zinc-500 font-medium">→</th>
                      <th className="px-3 py-2 text-left text-zinc-500 font-medium">Matched Account</th>
                      <th className="px-3 py-2 text-left text-zinc-500 font-medium">Matched Username</th>
                      <th className="px-3 py-2 text-left text-zinc-500 font-medium">Matched Brand</th>
                      <th className="px-3 py-2 text-left text-zinc-500 font-medium">Matched Partner</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                    {compareData.offerMatchLog.map((m, i) => (
                      <tr key={i} className="hover:bg-[hsl(225,15%,12%)]">
                        <td className="px-3 py-1.5 text-white">{m.invoiceBrand}</td>
                        <td className="px-3 py-1.5 text-zinc-400 max-w-[180px] truncate" title={m.invoiceOffer}>{m.invoiceOffer}</td>
                        <td className="px-3 py-1.5 text-zinc-400">{m.invoicePartner}</td>
                        <td className="px-3 py-1.5 text-purple-400">{m.invoiceGeo}</td>
                        <td className="px-3 py-1.5 text-right text-zinc-300 tabular-nums">{fmtCurrency(m.invoiceRevenue)}</td>
                        <td className="px-3 py-1.5 text-emerald-400 text-center">→</td>
                        <td className="px-3 py-1.5 text-emerald-400 tabular-nums">{m.matchedAccountId}</td>
                        <td className="px-3 py-1.5 text-white">{m.matchedUsername}</td>
                        <td className="px-3 py-1.5 text-zinc-400">{m.matchedBrand}</td>
                        <td className="px-3 py-1.5 text-zinc-400">{m.matchedPartner}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Exchange Rates */}
          <div className="bg-[hsl(225,15%,8%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
            <p className="text-xs text-zinc-500">
              Exchange rates used: {Object.entries(compareData.exchangeRates).map(([cur, rate]) =>
                `1 ${cur} = ${Number(rate).toFixed(4)} EUR`
              ).join(', ')} (averages from Google Sheet)
            </p>
          </div>
        </div>
      )}

      {/* Link Modal */}
      {linkModalIssue && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setLinkModalIssue(null)}>
          <div className="bg-[hsl(225,15%,10%)] border border-[hsl(225,15%,20%)] rounded-xl w-full max-w-lg max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-[hsl(225,15%,18%)]">
              <div>
                <h3 className="text-sm font-semibold text-white">Link Invoice to Recon Account</h3>
                <p className="text-[11px] text-zinc-400 mt-0.5">
                  {linkModalIssue.brand} — {linkModalIssue.offer} ({linkModalIssue.geo}) · {fmtCurrency(linkModalIssue.invoiceRevenue || 0)}
                </p>
              </div>
              <button onClick={() => setLinkModalIssue(null)} className="text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
            </div>

            <div className="p-3 border-b border-[hsl(225,15%,18%)]">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
                <input
                  type="text"
                  value={linkSearch}
                  onChange={e => setLinkSearch(e.target.value)}
                  placeholder="Search recon accounts..."
                  autoFocus
                  className="pl-8 pr-3 py-1.5 w-full text-xs bg-[hsl(225,15%,8%)] border border-[hsl(225,15%,20%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-purple-500/50"
                />
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-2">
              {filteredReconAccounts.length === 0 ? (
                <p className="text-center text-zinc-500 text-xs py-6">No matching accounts</p>
              ) : (
                <div className="space-y-1">
                  {filteredReconAccounts.map(a => (
                    <button
                      key={`${a.accountId}-${a.username}`}
                      onClick={() => handleLinkSave(a)}
                      disabled={linkSaving}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-purple-500/10 border border-transparent hover:border-purple-500/30 transition-colors flex items-center gap-3 group disabled:opacity-50"
                    >
                      <span className="text-xs text-zinc-500 tabular-nums w-14 shrink-0">{a.accountId}</span>
                      <span className="text-xs text-white font-medium truncate flex-1">{a.username || '—'}</span>
                      <span className="text-[10px] text-zinc-400 truncate max-w-[100px]">{a.partner}</span>
                      <span className="text-[10px] text-purple-400 font-medium w-6 shrink-0">{a.geo}</span>
                      <Link2 className="h-3 w-3 text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {linkSaving && (
              <div className="p-3 border-t border-[hsl(225,15%,18%)] flex items-center justify-center gap-2 text-xs text-zinc-400">
                <Loader2 className="h-3 w-3 animate-spin" /> Saving mapping...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const SummaryCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = ({ label, value, icon, color }) => {
  const colorMap: Record<string, string> = {
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
    purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
    cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
    white: 'text-white bg-white/5 border-white/10',
    zinc: 'text-zinc-300 bg-[hsl(225,15%,12%)] border-[hsl(225,15%,18%)]',
  };
  const c = colorMap[color] || colorMap.zinc;
  const parts = c.split(' ');
  return (
    <div className={`rounded-xl border p-3 ${parts.slice(1).join(' ')}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={parts[0]}>{icon}</span>
        <span className="text-[10px] text-zinc-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-base sm:text-lg font-bold tabular-nums ${parts[0]}`}>{value}</p>
    </div>
  );
};

const SeverityBadge: React.FC<{ severity: string }> = ({ severity }) => {
  const config: Record<string, { label: string; cls: string }> = {
    critical: { label: 'Critical', cls: 'bg-red-500/10 text-red-400 border-red-500/20' },
    high: { label: 'High', cls: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
    medium: { label: 'Medium', cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
    low: { label: 'Low', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
    info: { label: 'Info', cls: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' },
  };
  const c = config[severity] || config.info;
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium ${c.cls}`}>
      {c.label}
    </span>
  );
};

export default RaketechAccounts;
